import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import ClinicLayout from "../ClinicLayout";
import PatientVisits from "../PatientVisits";
import { PatientBookContent } from "../PatientBookPage";
import PatientOverview from "./PatientOverview";
import PatientProfile from "./PatientProfile";
import ClubLifeFeed from "./ClubLifeFeed";
import ClientAppShell from "./ClientAppShell";
import { ClientAthleteProvider } from "../../../context/ClientAthleteContext";
import ClientCalendar from "./ClientCalendar";
import ClientLife from "./ClientLife";
import ClientProfileRoutes from "./ClientProfileRoutes";
import ClientStats from "./ClientStats";
import ClientNotifications from "./ClientNotifications";
import ClientChatRoutes from "./ClientChatRoutes";
import ClientMembers from "./ClientMembers";
import ClientMemberProfile from "./ClientMemberProfile";
import ClientInstallBanner from "./ClientAppBanners";
import PwaUpdateBanner from "../common/PwaUpdateBanner";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { useIsClientApp } from "../../../hooks/useStandalone";
import { useSelector } from "react-redux";
import { normalizeRole } from "../../../utils/roles";
import { ToastProvider } from "../../ui";
import { redirectForClientRoute } from "../../../utils/clientNav";
import s from "./clientApp.module.css";

const PAGE_META = {
  "/app": { title: null, subtitle: null },
  "/app/calendar": { title: "Календарь", subtitle: "Расписание и подтверждение явки" },
  "/app/schedule": { title: "Календарь", subtitle: "Расписание и подтверждение явки" },
  "/app/life": { title: "Клуб", subtitle: "О клубе и лента новостей" },
  "/app/members": { title: "Участники", subtitle: "Люди из ваших групп" },
  "/app/profile": { title: "Профиль", subtitle: "Ваша страница" },
  "/app/profile/account": { title: "Настройки", subtitle: "Абонемент и аккаунт" },
  "/app/stats": { title: "Рейтинг", subtitle: "Посещаемость и место в группе" },
  "/app/notifications": { title: "Уведомления", subtitle: "Опросы и важное" },
  "/app/chat": { title: "Сообщения", subtitle: "Чат с тренером и клубом" },
  "/app/book": { title: "Запись", subtitle: null },
  "/app/visits": { title: "Занятия", subtitle: null },
};

function ClientFeatureRedirect() {
  const location = useLocation();
  const navigate = useNavigate();
  const { entitlements, tenant, billing } = useSelector((state) => state.auth);

  useEffect(() => {
    const to = redirectForClientRoute(
      location.pathname,
      entitlements,
      tenant,
      billing,
    );
    if (to) navigate(to, { replace: true });
  }, [location.pathname, entitlements, tenant, billing, navigate]);

  return null;
}

function pageMeta(pathname, isClientApp) {
  const base = pathname.replace(/\/$/, "") || "/app";
  if (isClientApp && base === "/app") {
    return { title: null, subtitle: null };
  }
  if (isClientApp && base.startsWith("/app/profile/account")) {
    return PAGE_META["/app/profile/account"];
  }
  if (isClientApp && base.startsWith("/app/profile")) {
    return PAGE_META["/app/profile"];
  }
  return PAGE_META[base] || PAGE_META["/app"];
}

function ScheduleBookRoute({ appMode }) {
  const navigate = useNavigate();
  return (
    <PatientBookContent
      embedded
      appMode={appMode}
      onBooked={() => navigate("/app/visits")}
    />
  );
}

function PortalRoutes({ appMode }) {
  if (appMode) {
    return (
      <Routes>
        <Route index element={<PatientOverview />} />
        <Route path="calendar" element={<ClientCalendar />} />
        <Route path="life" element={<ClientLife />} />
        <Route path="members" element={<ClientMembers />} />
        <Route path="members/:userId" element={<ClientMemberProfile />} />
        <Route path="profile/*" element={<ClientProfileRoutes />} />
        <Route path="stats" element={<ClientStats />} />
        <Route path="schedule" element={<Navigate to="/app/calendar" replace />} />
        <Route path="schedule/book" element={<Navigate to="/app/calendar" replace />} />
        <Route path="book" element={<Navigate to="/app/calendar" replace />} />
        <Route path="visits" element={<Navigate to="/app/calendar" replace />} />
        <Route path="athletes" element={<Navigate to="/app/profile" replace />} />
        <Route path="notifications" element={<ClientNotifications />} />
        <Route path="chat/*" element={<ClientChatRoutes />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route index element={<PatientOverview />} />
      <Route path="book" element={<ScheduleBookRoute appMode={false} />} />
      <Route path="visits" element={<PatientVisits embedded />} />
      <Route path="profile" element={<PatientProfile />} />
      <Route path="life" element={<ClubLifeFeed />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

export default function PatientPortal() {
  const { pathname } = useLocation();
  const isMobile = useIsMobile(768);
  const role = useSelector((state) => state.auth.role);
  const isClientApp = useIsClientApp(normalizeRole(role) === "client");
  const meta = pageMeta(pathname, isClientApp);

  const content = (
    <div className={s.portalWrap}>
      <ClientFeatureRedirect />
      {isClientApp && isMobile ? (
        <>
          <PwaUpdateBanner />
          <ClientInstallBanner />
        </>
      ) : null}
      <PortalRoutes appMode={isClientApp} />
    </div>
  );

  if (isClientApp) {
    return (
      <ToastProvider>
        <ClientAthleteProvider>
          <ClientAppShell>{content}</ClientAppShell>
        </ClientAthleteProvider>
      </ToastProvider>
    );
  }

  return (
    <ClinicLayout title={meta.title} subtitle={meta.subtitle}>
      {content}
    </ClinicLayout>
  );
}
